import { acquireEndpoint } from "utils/SmartMerchandiserAPI";
import {
  ADD_LINKED_CATEGORIES_SET,
  FETCH_ALL_LINKED_CATEGORIES,
  FETCH_LINKED_CATEGORY_SET_BY_CATEGORY,
  LINKED_CATEGORY_SET_NOT_FOUND,
  LINKED_CATEGORIES_SERVICE_URL,
  LinkedCategorySetByCategoryAction,
  LinkedCategorySetsRequestAction,
  SEARCH_LINKED_CATEGORIES,
  LinkedCategoryPane,
  LinkedCategorySearchAction,
  FETCH_LINKED_CATEGORY_CODES,
  LinkedCategoryCodesAction,
} from "./LinkedCategoriesTypes";

import {
  takeEvery,
  takeLatest,
  call,
  put,
  select,
} from "typed-redux-saga/macro";
import { callApi } from "utils/SagaUtils";
import { getData, postData } from "services/ApiService";
import { addGlobalAlertState } from "store/global-alert/GlobalAlertActions";
import { resetUploadSuccess } from "./LinkedCategoriesActions";
import {
  selectCurrentLocale,
  selectStoreList,
} from "store/store-list/StoreListSelectors";
import { selectAllCatalogs } from "store/catalog/CatalogSelectors";

function* fetchAllLinkedCategories() {
  try {
    const actionType = FETCH_ALL_LINKED_CATEGORIES;
    const serviceType = LINKED_CATEGORIES_SERVICE_URL;
    const constName = Object.keys({
      FETCH_ALL_LINKED_CATEGORIES,
    })[0].toString();
    const endpoint = acquireEndpoint(constName, serviceType);
    yield* call(callApi, actionType, getData, {}, endpoint);
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_ALL_LINKED_CATEGORIES.FAILURE,
      message: e.message,
    });
  }
}

function* addLinkedCategorySets(action: LinkedCategorySetsRequestAction) {
  try {
    // Validate for duplicate storeId/catalogId combinations within each linked category set
    const { linkedCategorySets, isLinkRequest, isUnlinkRequest, removedCount } =
      action.payload;
    const fromSearchTab = isLinkRequest || isUnlinkRequest;

    for (const set of linkedCategorySets) {
      const duplicateStoreCatalogKeys = new Set<string>();
      const catalogCategoryMap = new Map<string, string>(); // catalogId -> categoryId

      for (const category of set.categories) {
        const key = `${category.storeId}:${category.catalogId}`;
        // Only the upload tool rejects a repeated store/catalog; the search
        // tab links whatever the two panes hold.
        if (!fromSearchTab && duplicateStoreCatalogKeys.has(key)) {
          // Show error alert for duplicate storeId/catalogId combination
          yield* put(
            addGlobalAlertState({
              alertsProps: [
                {
                  descriptor: {
                    id: "linkedCategories.duplicateStoreCatalog",
                    defaultMessage: `Duplicate storeId/catalogId combination not allowed within the same linked category set. Found duplicate: {storeId}/{catalogId}`,
                  },
                  variables: {
                    storeId: category.storeId,
                    catalogId: category.catalogId,
                  },
                  severity: "error",
                  variant: "standard",
                },
              ],
            }),
          );
          yield* put(resetUploadSuccess());
          return;
        }
        duplicateStoreCatalogKeys.add(key);

        // Check for invalid linking: different categories from same catalog across different stores
        if (!fromSearchTab && catalogCategoryMap.has(category.catalogId)) {
          const existingCategory = catalogCategoryMap.get(category.catalogId);
          if (existingCategory && existingCategory !== category.categoryId) {
            // Same catalog, different categories - this is invalid
            yield* put(
              addGlobalAlertState({
                alertsProps: [
                  {
                    descriptor: {
                      id: "linkedCategories.invalidCatalogLinking",
                      defaultMessage: `Cannot link different categories ({existingCategory} and {categoryId}) from the same catalog ({catalogId}).`,
                    },
                    variables: {
                      existingCategory: existingCategory || "",
                      categoryId: category.categoryId,
                      catalogId: category.catalogId,
                    },
                    severity: "error",
                    variant: "standard",
                  },
                ],
              }),
            );
            yield* put(resetUploadSuccess());
            return;
          }
        } else {
          catalogCategoryMap.set(category.catalogId, category.categoryId);
        }
      }
    }

    const actionType = ADD_LINKED_CATEGORIES_SET;
    const serviceType = LINKED_CATEGORIES_SERVICE_URL;
    const constName = Object.keys({
      ADD_LINKED_CATEGORIES_SET,
    })[0].toString();
    const endpoint = acquireEndpoint(constName, serviceType);
    const response = yield* call(
      callApi,
      actionType,
      postData,
      action.payload,
      endpoint,
    );
    if (response.type === actionType.SUCCESS) {
      // The sets drive both panes, so reload them before reporting success.
      // The upload tool reloads them itself, so this is only for the search tab.
      if (fromSearchTab) {
        yield* put({ type: FETCH_ALL_LINKED_CATEGORIES.REQUEST });
      }
      yield* put(
        addGlobalAlertState({
          alertsProps: [
            {
              descriptor: isUnlinkRequest
                ? {
                    id: "linkedCategoriesSearch.categoriesRemoved",
                    defaultMessage:
                      "{count, plural, one {# category} other {# categories}} removed.",
                  }
                : isLinkRequest
                  ? {
                      id: "linkedCategories.linkComplete",
                      defaultMessage: "Link categories updated",
                    }
                  : {
                      id: "linkedCategories.uploadComplete",
                      defaultMessage: "Upload Complete!",
                    },
              variables: isUnlinkRequest
                ? { count: removedCount ?? 0 }
                : undefined,
              severity: "success",
              variant: "standard",
            },
          ],
        }),
      );
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: ADD_LINKED_CATEGORIES_SET.FAILURE,
      message: e.message,
    });
  }
}

function* fetchLinkedCategorySetByCategory(
  action: LinkedCategorySetByCategoryAction,
) {
  try {
    const actionType = FETCH_LINKED_CATEGORY_SET_BY_CATEGORY;
    const serviceType = LINKED_CATEGORIES_SERVICE_URL;
    const constName = Object.keys({
      FETCH_LINKED_CATEGORY_SET_BY_CATEGORY,
    })[0].toString();
    const { catalogId, categoryId } = action.payload;
    const endpoint = acquireEndpoint(
      constName,
      serviceType,
      catalogId,
      categoryId,
    );
    const optionsObj = {
      showGenericError: false,
    };
    const result = yield* call(
      callApi,
      actionType,
      getData,
      null,
      endpoint,
      {},
      optionsObj,
    );
    if (
      result &&
      result.type === actionType.FAILURE &&
      result.reason !== LINKED_CATEGORY_SET_NOT_FOUND
    ) {
      yield* put(
        addGlobalAlertState({
          alertsProps: [
            {
              descriptor: {
                id: "linkedCategories.getLinkedCategorySetByCategoryFailure",
                defaultMessage:
                  "Unable to fetch Linked Category Sets for the current category",
              },
              severity: "error",
              variant: "standard",
            },
          ],
        }),
      );
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_LINKED_CATEGORY_SET_BY_CATEGORY.FAILURE,
      message: e.message,
    });
  }
}

function* searchLinkedCategories(action: LinkedCategorySearchAction) {
  try {
    const actionType = SEARCH_LINKED_CATEGORIES;
    const { pane, storeId, catalogId, startsWith, limit } = action.payload;
    const localeCode = yield* select(selectCurrentLocale);
    const constName = Object.keys({
      SEARCH_LINKED_CATEGORIES,
    })[0].toString();
    const endpoint = acquireEndpoint(constName, startsWith, limit);
    const headersObj = {
      "x-locale-code": localeCode || "default",
      "x-currency-code": "USD",
      "x-store-id": storeId,
      "x-catalog-id": catalogId,
    };
    yield* call(callApi, actionType, getData, null, endpoint, headersObj, {
      showGenericError: false,
      meta: { pane },
    });
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: SEARCH_LINKED_CATEGORIES.FAILURE,
      meta: { pane: action.payload.pane },
      message: e.message,
    });
  }
}

// The stored sets carry ids only, so each member is labelled from the same
// category, catalog and store codes the search response would have carried.
function* fetchLinkedCategoryCodes(action: LinkedCategoryCodesAction) {
  try {
    const { categories } = action.payload;
    const localeCode = yield* select(selectCurrentLocale);
    const catalogs = yield* select(selectAllCatalogs);
    const stores = yield* select(selectStoreList);
    const constName = "FETCH_LINKED_CATEGORY_CODES";
    const responses = yield* call(() =>
      Promise.all(
        categories.map((category) =>
          getData(null, acquireEndpoint(constName, category.categoryId), {
            "x-locale-code": localeCode || "default",
            "x-currency-code": "USD",
            "x-store-id": category.storeId,
            "x-catalog-id": category.catalogId,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          }).then((res: any) => [
            `${category.storeId}:${category.catalogId}:${category.categoryId}`,
            {
              code: res?.data?.code ?? "",
              catalogCode:
                catalogs.find((c) => c.id === category.catalogId)?.code ?? "",
              storeCode:
                stores.find((s) => s.storeId === category.storeId)?.code ?? "",
            },
          ]),
        ),
      ),
    );
    yield* put({
      type: FETCH_LINKED_CATEGORY_CODES.SUCCESS,
      // Every member looked up is recorded, including those the platform could
      // not name, so a missing code is not retried on every render.
      payload: Object.fromEntries(responses),
    });
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_LINKED_CATEGORY_CODES.FAILURE,
      message: e.message,
    });
  }
}

export function* watchFetchLinkedCategoryCodes() {
  yield* takeLatest(
    FETCH_LINKED_CATEGORY_CODES.REQUEST,
    fetchLinkedCategoryCodes,
  );
}

export function* watchFetchAllLinkedCategories() {
  yield* takeEvery(
    FETCH_ALL_LINKED_CATEGORIES.REQUEST,
    fetchAllLinkedCategories,
  );
}

export function* watchAddLinkedCategorySets() {
  yield* takeEvery(ADD_LINKED_CATEGORIES_SET.REQUEST, addLinkedCategorySets);
}

export function* watchFetchLinkedCategorySetByCategory() {
  yield* takeEvery(
    FETCH_LINKED_CATEGORY_SET_BY_CATEGORY.REQUEST,
    fetchLinkedCategorySetByCategory,
  );
}

// One watcher per pane so a search in one pane cannot cancel the other's.
const isSearchFor = (pane: LinkedCategoryPane) => (action: any) =>
  action.type === SEARCH_LINKED_CATEGORIES.REQUEST &&
  action.payload?.pane === pane;

export function* watchSearchTargetLinkedCategories() {
  yield* takeLatest(isSearchFor("target"), searchLinkedCategories);
}

export function* watchSearchSourceLinkedCategories() {
  yield* takeLatest(isSearchFor("source"), searchLinkedCategories);
}
